import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaImage, FaTimes } from 'react-icons/fa';
import { useSecureMediaUrl } from '../hooks/useSecureMediaUrl';

export default function TelemetryImagePreview({ source, path, children }) {
  const { toSecureMediaUrl } = useSecureMediaUrl();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({});
  const [imageState, setImageState] = useState('loading');
  const trigger = useRef(null);
  const panel = useRef(null);
  const openTimer = useRef();
  const closeTimer = useRef();
  const panelId = useId();
  // Telemetry uses dated paths (e.g. 20261006/file.png), while the media helper
  // accepts screenshot paths and resolves them through the authenticated API.
  const mediaSource = /^\d{8}\//.test(source) ? `screenshots/${source}` : source;

  const clearTimers = () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); };
  const close = () => { clearTimers(); setOpen(false); };
  const show = () => {
    clearTimers();
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(360, window.innerWidth - 24);
    const height = Math.min(320, window.innerHeight - 24);
    const right = rect.right + 12;
    const left = right + width <= window.innerWidth - 12 ? right :
      rect.left - width - 12 >= 12 ? rect.left - width - 12 : (window.innerWidth - width) / 2;
    const top = Math.max(12, Math.min(rect.top - 40, window.innerHeight - height - 12));
    setPosition({ left, top, width, height });
    setImageState('loading');
    setOpen(true);
  };
  const scheduleClose = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  };

  useEffect(() => () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); }, []);
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') dismiss(); };
    const onOutside = (event) => {
      if (!trigger.current?.contains(event.target) && !panel.current?.contains(event.target)) dismiss();
    };
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onOutside);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onOutside);
    };
  }, [open]);

  return <>
    <button
      ref={trigger}
      type="button"
      className="dt-image-trigger"
      aria-label={`Preview image for ${path}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      onMouseEnter={() => { clearTimers(); if (!open) openTimer.current = setTimeout(show, 180); }}
      onMouseLeave={scheduleClose}
      onFocus={() => { if (!open) show(); }}
      onBlur={(event) => { if (!panel.current?.contains(event.relatedTarget)) scheduleClose(); }}
      onClick={() => { if (!open) show(); }}
    >
      {children}
      <span className="dt-image-hint"><FaImage aria-hidden="true" /> Hover to preview</span>
    </button>
    {open && createPortal(
      <div
        ref={panel}
        id={panelId}
        role="dialog"
        aria-label="Image preview"
        className="dt-image-panel"
        style={position}
        onMouseEnter={clearTimers}
        onMouseLeave={scheduleClose}
        onFocus={clearTimers}
        onBlur={(event) => { if (!panel.current?.contains(event.relatedTarget) && event.relatedTarget !== trigger.current) scheduleClose(); }}
      >
        <div className="dt-image-panel-header"><span><FaImage aria-hidden="true" /> Image preview</span><button type="button" aria-label="Close image preview" onClick={close}><FaTimes /></button></div>
        <div className="dt-image-panel-body" aria-busy={imageState === 'loading'}>
          {imageState === 'loading' && <span className="dt-image-message" role="status">Loading image…</span>}
          {imageState === 'error' ? <span className="dt-image-message" role="status">Image unavailable. The file may have expired or access may be restricted.</span> :
            <img src={toSecureMediaUrl(mediaSource)} alt={`Telemetry capture from ${path}`} referrerPolicy="no-referrer" onLoad={() => setImageState('loaded')} onError={() => setImageState('error')} style={{ visibility: imageState === 'loaded' ? 'visible' : 'hidden' }} />}
        </div>
        <div className="dt-image-panel-caption">{path}</div>
      </div>, document.body
    )}
  </>;
}
